import React, { useContext, useState } from 'react';
import { NotebookContext } from '../context/NotebookContext';
import client from '../lib/client';
import { trackerCreateOrUpdateMutation } from '../queries/trackers';
import {
  TrackerCreateOrUpdateMutation,
  TrackerCreateOrUpdateMutationVariables
} from '../queries/__generated__/TrackerCreateOrUpdateMutation';
import ErrorMessages from './ErrorMessages';
import { Input } from './forms';
import TrackerTypeSelect from './forms/TrackerTypeSelect';
import { Modal } from './ui';
import { TrackerType as APITrackerType } from '../__generated__/globalTypes';
import { PureQueryOptions } from 'apollo-client';
import { subjectBySlugQuery } from '../queries/subjects';
import { groupBySlugQuery } from '../queries/groups';
import { selectedNotebookQuery } from '../queries/notebooks';
import { SelectedNotebookVariables } from '../queries/__generated__/SelectedNotebook';
import { SubjectBySlugQueryVariables } from '../queries/__generated__/SubjectBySlugQuery';
import { GroupBySlugQueryVariables } from '../queries/__generated__/GroupBySlugQuery';
import { toast } from 'react-toastify';

interface NewTrackerModalProps {
  closeModal: () => void;
  group?: {
    id: string;
    slug: string;
  };
  subject?: {
    id: string;
    slug: string;
  };
}

const NewTrackerModal: React.FC<NewTrackerModalProps> = ({ closeModal, group, subject }) => {
  const [trackerName, setTrackerName] = useState('');
  const [trackerType, setTrackerType] = useState<TrackerType>('FIVER');
  const [errors, setErrors] = useState<{ message: string }[]>([]);
  const { notebook, reloadNotebook } = useContext(NotebookContext);

  async function createTracker() {
    let variables: TrackerCreateOrUpdateMutationVariables = {
      input: {
        notebookId: notebook.id,
        name: trackerName,
        type: APITrackerType[trackerType]
      }
    };

    let refetchQueries: PureQueryOptions[] = [
      { query: selectedNotebookQuery, variables: { id: notebook.id } as SelectedNotebookVariables }
    ];

    if (subject) {
      variables.input.subjectId = subject.id;
      refetchQueries.push({
        query: subjectBySlugQuery,
        variables: { slug: subject.slug, notebookId: notebook.id } as SubjectBySlugQueryVariables
      });
    }

    if (group) {
      variables.input.groupId = group.id;
      refetchQueries.push({
        query: groupBySlugQuery,
        variables: { slug: group.slug, notebookId: notebook.id } as GroupBySlugQueryVariables
      });
    }

    const res = await client.mutate<
      TrackerCreateOrUpdateMutation,
      TrackerCreateOrUpdateMutationVariables
    >({
      mutation: trackerCreateOrUpdateMutation,
      variables,
      refetchQueries,
      awaitRefetchQueries: true
    });

    const { errors } = res.data!.createOrUpdateTracker;
    if (errors) {
      setErrors(errors);
      return;
    }

    toast(`New tracker created`, { type: 'success' });
    reloadNotebook();
    closeModal();
  }

  return (
    <Modal closeModal={closeModal}>
      <div className="pa4 navy">
        <div className="tc">
          <h1 className="f4 fw6 mb2">New Tracker</h1>
          <p className="f7 serif lh-copy measure-narrow mt0 mb2 center">
            Trackers are questions or metrics that help you learn about your Subjects.
          </p>
        </div>

        <div className="mb3 tc">
          <ErrorMessages errors={errors} />
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            if (trackerName) {
              createTracker();
            }
          }}
        >
          <Input
            value={trackerName}
            onChange={e => setTrackerName(e.target.value)}
            wrapperClassName="mb2"
            placeholder="Tracker name..."
            data-testid="tracker-name-input"
            autoFocus
          />
          <TrackerTypeSelect
            selectedType={trackerType as TrackerType}
            setTrackerType={setTrackerType}
          />
        </form>
      </div>

      <div className="flex bt b--light-gray navy">
        <button
          className="w-50 br b--light-gray f6 pa3 tc blue hover-navy"
          onClick={closeModal}
          data-testid="cancel-button"
        >
          Cancel
        </button>

        <button
          className="w-50 f6 pa3 tc blue hover-navy"
          onClick={createTracker}
          disabled={!trackerName}
          data-testid="create-tracker-button"
        >
          Create Tracker
        </button>
      </div>
    </Modal>
  );
};

export default NewTrackerModal;
